import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { type CardDispute, CardDisputeNextAction, cardsGetDisputeOptions } from '@column/api';

import { useSessionStore } from '~/stores/Session';

const AWAIT_NETWORK_SUBMISSION_REFETCH_INTERVAL_MS = 20_000;
// Bounded polling: Visa sync runs roughly every 30 minutes, so stop waiting on the browser after
// this window and fall back to the explicit refresh affordance.
const AWAIT_NETWORK_SUBMISSION_MAX_POLL_MS = 10 * 60 * 1000;

export const shouldPollWhileAwaitingNetworkSubmission = (dispute?: CardDispute | null): boolean =>
  Boolean(dispute?.next_actions?.includes(CardDisputeNextAction.AWAIT_NETWORK_SUBMISSION));

export const useCardDispute = ({ disputeId }: { disputeId?: string }) => {
  const { currentPlatform } = useSessionStore();
  const pollingSince = useRef<Map<string, number>>(new Map());
  const [, forceUpdate] = useState({});

  useEffect(() => {
    // Re-evaluate the polling decision when the tab becomes visible again.
    const onVisibilityChange = () => forceUpdate({});
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    return () => {
      if (disputeId) {
        pollingSince.current.delete(disputeId);
      }
    };
  }, [disputeId]);

  return useQuery({
    ...cardsGetDisputeOptions({ path: { card_dispute_id: disputeId ?? '' } }),
    enabled: Boolean(disputeId && currentPlatform?.id),
    refetchInterval: (query) => {
      const dispute = query.state.data as CardDispute | undefined;
      if (!disputeId || !shouldPollWhileAwaitingNetworkSubmission(dispute)) {
        if (disputeId) {
          pollingSince.current.delete(disputeId);
        }
        return false;
      }
      if (document.visibilityState !== 'visible') {
        pollingSince.current.delete(disputeId);
        return false;
      }
      const now = Date.now();
      const since = pollingSince.current.get(disputeId) ?? now;
      if (now - since >= AWAIT_NETWORK_SUBMISSION_MAX_POLL_MS) {
        pollingSince.current.delete(disputeId);
        return false;
      }
      pollingSince.current.set(disputeId, since);
      return AWAIT_NETWORK_SUBMISSION_REFETCH_INTERVAL_MS;
    },
  });
};
